import { BookingPill } from "PFApp/components/pills/booking_pill";
import { Typography } from "PFComponents/typography";
import { BookingHistoryProfile } from "PFCore/hooks/queries/bookings/history/use_booking_history";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { AccessLevel } from "PFTypes";
import { ReactNode } from "react";

import { AttributeKey, AttributeValue } from "../../../../../../types/booking_history_item";
import css from "./attribute.module.scss";
import { BooleanAttribute } from "./boolean_attribute";
import { DateAttribute } from "./date_attribute";
import { DurationAttribute } from "./duration_attribute";
import { LoadAttribute } from "./load_attribute";
import { ProfileAttribute } from "./profile_attribute";
import { RequirementType, RequirementTypeAttribute } from "./requirement_type_attribute";

type UseAttribute = {
  attributeId: AttributeKey;
  metaProfiles?: Record<number, BookingHistoryProfile>;
};

type UseAttributeReturn = {
  attributeRenderer: (value: AttributeValue) => ReactNode | null;
};

export const useAttribute = ({ attributeId, metaProfiles }: UseAttribute): UseAttributeReturn => {
  const { getBookingCategory } = useBookingCategories();

  const attributeRenderer = (value: AttributeValue) => {
    if (value === null) {
      return null;
    }

    switch (attributeId) {
      case "bookingCategoryId": {
        const category = getBookingCategory(Number(value));
        const isCategoryHidden = !category || category.bookings_permissions === AccessLevel.Hidden;
        return (
          <BookingPill
            category={!isCategoryHidden ? category : undefined}
            className={css.bookingPill}
            style={{ borderWidth: 1 }}
            iconSize="xxs"
            center
          >
            <Typography withMargin variant="labelRegular" tag="span" textOverflow="clip">
              {category?.display_as ?? ""}
            </Typography>
          </BookingPill>
        );
      }
      case "profileId": {
        return <ProfileAttribute profileId={value as number} metaProfiles={metaProfiles} />;
      }
      case "requirementValue":
      case "duration": {
        return <DurationAttribute value={value as number} />;
      }
      case "calculatedLoad": {
        return <LoadAttribute value={value as number} />;
      }
      case "startDate":
      case "endDate": {
        return <DateAttribute value={value as string} />;
      }
      case "requirementType": {
        return <RequirementTypeAttribute value={value as RequirementType} />;
      }
      default: {
        if (typeof value === "boolean") {
          return <BooleanAttribute value={value} />;
        }
        return (
          <Typography withMargin variant="bodyRegular" tag="span">
            {value}
          </Typography>
        );
      }
    }
  };

  return {
    attributeRenderer
  };
};
